import ConditionalLoader from '@Guidelines/Component/Loader/ConditionalLoader';
import SectionCard from '@Guidelines/Component/SectionCard';
import type SubjectTypeDefinitionFormData from '@UI/Pages/Administration/SubjectTypes/SubjectTypeEdit/FormData/SubjectTypeDefinitionFormData';
import * as React from 'react';
import { Grid, GridItem } from '@verclam/grid-system';
import { useSelector } from '@verclam/synapse';
import { Checkbox, type CheckboxValue, Tag } from '@verclam/ui-kit';

type Props = {
	readonly formData: SubjectTypeDefinitionFormData;
	readonly onChange: (
		update: (formData: SubjectTypeDefinitionFormData) => SubjectTypeDefinitionFormData,
	) => void;
	readonly isReadOnly: boolean;
};

/**
 * The workflows offered for a dossier of this type, among those of the account. A key the account no
 * longer lists is not displayed but stays in the draft, so a save keeps it.
 */
const WorkflowsSection: React.FC<Props> = ({ formData, onChange, isReadOnly }) => {
	const workflows = useSelector((state) => state.subjectTypeDefinitionsStore.workflows);
	const serverMessage = formData.getWorkflowKeysServerMessage();

	const handleToggle = React.useCallback(
		(checked: boolean, value: CheckboxValue | undefined) => {
			if (typeof value !== 'string') {
				return;
			}

			onChange((current) =>
				current.setWorkflowKeys(
					checked
						? [...current.workflowKeys, value]
						: current.workflowKeys.filter((key) => key !== value),
				),
			);
		},
		[onChange],
	);

	return (
		<SectionCard
			title="Workflows proposés"
			description="Décocher un workflow automatique l'arrête pour les prochains dépôts de ce type."
		>
			{(workflows?.list ?? []).map((workflow) => (
				<Grid key={workflow.key} columns={12} columnGap={1} alignItems="center">
					<GridItem columnSize={{ xs: 8, xl: 4 }}>
						<Checkbox
							name="workflowKeys"
							label={workflow.label}
							value={workflow.key}
							checked={formData.workflowKeys.includes(workflow.key)}
							disabled={isReadOnly}
							onChange={handleToggle}
						/>
					</GridItem>
					<GridItem columnSize={{ xs: 4, xl: 8 }}>
						<ConditionalLoader data={workflow.isAutomatic}>
							<Tag value="Automatique" severity="info" rounded />
						</ConditionalLoader>
						<ConditionalLoader data={!workflow.isActive}>
							<Tag value="Désactivé" severity="secondary" rounded />
						</ConditionalLoader>
					</GridItem>
				</Grid>
			))}
			<ConditionalLoader data={serverMessage}>
				{(message) => <small className="ui-kit-field-error">{message}</small>}
			</ConditionalLoader>
		</SectionCard>
	);
};

export default WorkflowsSection;
